import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AuthService } from '../services/auth.service';
import {
  EXTERNAL_USER_APPROVED_ALLOWED_ROUTES,
  EXTERNAL_USER_APPROVED_BLOCKED_ROUTES,
  EXTERNAL_USER_PENDING_ALLOWED_ROUTES,
} from '../config/external-user-routes.config';

@Injectable({
  providedIn: 'root',
})
export class ExternalUserAccessGuard implements CanActivate, CanActivateChild {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    return this.checkAccess(state.url);
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    return this.checkAccess(state.url);
  }

  private checkAccess(url: string): boolean | UrlTree {
    this.auth.loadEntityData();

    if (!this.auth.isExternalUser()) {
      return true;
    }

    const normalizedUrl = this.normalizeUrl(url);

    if (this.auth.isApprovedExternalUser()) {
      if (
        this.matchesAny(normalizedUrl, EXTERNAL_USER_APPROVED_BLOCKED_ROUTES)
        || !this.matchesAny(normalizedUrl, EXTERNAL_USER_APPROVED_ALLOWED_ROUTES)
      ) {
        return this.router.createUrlTree(['/dashboard']);
      }

      return true;
    }

    if (this.matchesAny(normalizedUrl, EXTERNAL_USER_PENDING_ALLOWED_ROUTES)) {
      return true;
    }

    return this.router.createUrlTree(['/suppliers/register/new']);
  }

  private normalizeUrl(url: string): string {
    const path = url.split('?')[0].split('#')[0];
    return path.endsWith('/') && path !== '/' ? path.slice(0, -1) : path;
  }

  private matchesAny(url: string, prefixes: string[]): boolean {
    return prefixes.some((prefix) => url === prefix || url.startsWith(`${prefix}/`));
  }
}
